/**
 * Dashboard hook — TanStack Query wrapper for the dashboard API.
 *
 * useDashboard(): fetches GET /api/v1/dashboard.
 * Auto-refreshes every 5 minutes — dashboard metrics are meant to be a live view.
 *
 * The API returns role-filtered metrics — the same endpoint serves all roles.
 * The `role` field in the response confirms server/client role agreement.
 */

import { useQuery } from '@tanstack/react-query'
import { apiGet } from '@/lib/api'

// ── Metrics types per role ────────────────────────────────────────────────────

export interface AdminDashboardMetrics {
  totalClients: number
  totalAccounts: number
  activeAccounts: number
  mrrCents: number
  overdueInvoices: number
  totalQuotations: number
  pendingCommissionsCents: number
  totalResellers: number
  totalFreelancers: number
}

export interface SalesRepDashboardMetrics {
  myClients: number
  myQuotations: number
  myPendingQuotations: number
  myBillingCents: number
}

export interface ResellerDashboardMetrics {
  myClients: number
  myAccountsBillingCents: number
  pendingCommissionsCents: number
  approvedCommissionsCents: number
  paidCommissionsCents: number
  contractStatus: string | null
}

export interface FreelanceDashboardMetrics {
  myClients: number
  pendingCommissionsCents: number
  approvedCommissionsCents: number
  paidCommissionsCents: number
  contractStatus: string | null
}

// ── Union type for the API response ──────────────────────────────────────────

type DashboardRole = 'super_admin' | 'sales_admin' | 'sales_rep' | 'reseller' | 'freelance' | 'editor'

export type DashboardData =
  | { role: 'super_admin' | 'sales_admin'; metrics: AdminDashboardMetrics }
  | { role: 'sales_rep'; metrics: SalesRepDashboardMetrics }
  | { role: 'reseller'; metrics: ResellerDashboardMetrics }
  | { role: 'freelance'; metrics: FreelanceDashboardMetrics }
  | { role: 'editor'; metrics: Record<string, never> }

interface DashboardApiResponse {
  data: DashboardData
}

// ── Query key factory ─────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: ['dashboard'] as const,
  data: () => [...dashboardKeys.all, 'data'] as const,
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetch role-filtered dashboard metrics.
 * Auto-refreshes every 5 minutes (300_000 ms).
 * Returns the full { role, metrics } response so the panel can type-narrow on role.
 */
export function useDashboard() {
  return useQuery<DashboardData>({
    queryKey: dashboardKeys.data(),
    queryFn: async () => {
      const res = await apiGet<DashboardApiResponse>('/api/v1/dashboard')
      return res.data
    },
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000, // Consider fresh for 1 minute
  })
}
